import React, {useMemo, useState} from "react";
import {handleGraphQLError} from "../utils/graphqlErrorHandler";
import Slide from "@mui/material/Slide";
import useTriStateSort from "../hooks/useTriStateSort";
import ResponsiveSelect from "../components/ResponsiveSelect";
import PageHeader from "../components/PageHeader";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchField from "../components/SearchField";
import {formatToLocalTime} from "../utils/dateUtils";
import {Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper, Snackbar, Stack, Table, TableBody, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Toolbar, Tooltip, Typography, useMediaQuery} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import EllipsisCell from "../components/EllipsisCell";
import displayValue from "../utils/displayValue";
import sortOptionsByLabel from "../utils/sortOptionsByLabel";
import TablePaginationActions from "../components/TablePaginationActions";


import {GoogleMap, InfoWindow, Marker} from "@react-google-maps/api";

import AddressAutocomplete from "../components/AddressAutocomplete";
import {gql, useMutation, useQuery} from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import {useTranslation} from "react-i18next";

const SlideUp = (props) => <Slide {...props} direction="up"/>;

const PAGINATE_LOCATIONS = gql`
    query PaginateLocations(
        $pagination: Pagination!
        $sortBy: [SortBy!]
        $criteria: LocationCriteria
    ) {
        paginateLocation(
            pagination: $pagination
            sortBy: $sortBy
            criteria: $criteria
        ) {
            pageInfo {
                totalElements
                totalPages
                numberOfElements
                pageNumber
                pageSize
            }
            content {
                id
                name
                address {
                    fullAddress
                    latitude
                    longitude
                }
                type
                customer {
                    id
                    name
                }
                updatedTimestamp
                deleted
            }
        }
    }
`;


const FILTER_CUSTOMERS = gql`
    query FilterCustomer {
        filterCustomer(
            criteria: { deleted: false }
            sortBy: { field: "name", direction: ASC }
        ) {
            id
            name
        }
    }
`;


const CREATE_LOCATION = gql`
    mutation CreateLocation($input: LocationInput!) {
        createLocation(input: $input) {
            id
            name
            updatedTimestamp
            customer { id name }
        }
    }
`;

const UPDATE_LOCATION = gql`
    mutation UpdateLocation($id: ID!, $input: LocationInput!) {
        updateLocation(id: $id, input: $input) {
            id
            name
            updatedTimestamp
            deleted
        }
    }
`;

const DELETE_LOCATION = gql`
    mutation DeleteLocation($id: ID!) {
        deleteLocation(id: $id)
    }
`;

const Location = () => {
    const {t} = useTranslation();
    const isLoaded = true;

    const [mapDialogOpen, setMapDialogOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);


    const role = localStorage.getItem("role") || sessionStorage.getItem("role");
    const canEdit = role !== "CUSTOMER";
    const emptyStateColumns = canEdit ? 6 : 5;



    const [page, setPage] = useState(0);
    const [rowsPerPage, setRpp] = useState(10);
    const {sortFields, handleSort, getSortProps} = useTriStateSort(
        (updated) => {
            refetch({...variables, sortBy: updated});
        }
    );


    const [nameFilter, setNameFilter] = useState("");
    const [addressFilter, setAddressFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [customerFilter, setCustomerFilter] = useState("");
    const [debouncedFilters, setDebouncedFilters] = useState({
        name: "",
        address: "",
        type: "",
        customer: "",
    });


    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);


    const [createForm, setCreateForm] = useState({
        name: "",
        fullAddress: "",
        type: "",
        customer: "",
    });
    const [editForm, setEditForm] = useState({
        id: "",
        name: "",
        fullAddress: "",
        type: "",
        customer: "",
    });
    const [deleteId, setDeleteId] = useState("");


    const [snackbar, setSnackbar] = useState({open: false, message: "", severity: "success"});
    const [createError, setCreateError] = useState("");
    const [createApiError, setCreateApiError] = useState({name: "", general: ""});
    const [editApiError, setEditApiError] = useState({name: "", general: ""});
    const [editError, setEditError] = useState("");
    const [deleteError, setDeleteError] = useState("");


    const isSmall = useMediaQuery("(max-width:600px)");


    const openMapDialog = (location) => {
        if (!location?.address?.latitude || !location?.address?.longitude) return;
        setSelectedLocation(location);
        setMapDialogOpen(true);
    };

    const closeMapDialog = () => {
        setMapDialogOpen(false);
        setSelectedLocation(null);
    };


    const variables = useMemo(() => {
        const criteria = {
            deleted: false,
            ...(debouncedFilters.name ? {name: debouncedFilters.name} : {}),
            ...(debouncedFilters.address ? {address: {fullAddress: debouncedFilters.address}} : {}),
            ...(debouncedFilters.type ? {type: debouncedFilters.type} : {}),
            ...(debouncedFilters.customer ? {customer: debouncedFilters.customer} : {}),
        };

        return {
            pagination: {page, itemsPerPage: rowsPerPage},
            sortBy: sortFields,
            criteria,
        };
    }, [page, rowsPerPage, sortFields, debouncedFilters]);

    const {data, loading, error, refetch} = useQuery(PAGINATE_LOCATIONS, {
        variables,
        fetchPolicy: "no-cache",
    });


    const {data: customerData} = useQuery(FILTER_CUSTOMERS, {
        fetchPolicy: "no-cache",
    });



    const [createLocation, {loading: creating}] = useMutation(CREATE_LOCATION);
    const [updateLocation, {loading: updating}] = useMutation(UPDATE_LOCATION);
    const [deleteLocation, {loading: deleting}] = useMutation(DELETE_LOCATION);

    const pageInfo = data?.paginateLocation?.pageInfo;
    const rows = data?.paginateLocation?.content ?? [];

    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRpp(parseInt(e.target.value, 10));
        setPage(0);
    };


    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            setDebouncedFilters({
                name: nameFilter.trim(),
                address: addressFilter.trim(),
                type: typeFilter || "",
                customer: customerFilter || "",
            });
            setPage(0);
        }
    };

    const applyFilters = () => {
        setDebouncedFilters({
            name: nameFilter.trim(),
            address: addressFilter.trim(),
            type: typeFilter || "",
            customer: customerFilter || "",
        });
        setPage(0);
    };


    const fetchLatLng = async (address) => {
        if (!address?.trim()) return {lat: null, lng: null};
        const apiKey = "AIzaSyCNQlicydON-_g4zJgROSzg0qqr7fk4Flw";
        const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`;
        try {
            const res = await fetch(url);
            const j = await res.json();
            if (j.status === "OK" && j.results[0]) {
                const loc = j.results[0].geometry.location;
                return {lat: loc.lat, lng: loc.lng};
            }
        } catch (_ignored) {
            // Geocoding failure is non-critical
        }
        return {lat: null, lng: null};
    };

    const openEdit = (row) => {
        setEditForm({
            id: row.id,
            name: row.name || "",
            fullAddress: row.address?.fullAddress || "",
            latitude: row.address?.latitude || null,
            longitude: row.address?.longitude || null,
            type: row.type || "",
            customer: row.customer?.id || "",
        });
        setEditOpen(true);
    };

    const openDelete = (row) => {
        setDeleteId(row.id);
        setEditForm((prev) => ({...prev, name: row.name}));
        setDeleteOpen(true);
    };

    const handleCloseCreate = () => {
        setCreateOpen(false);
        setCreateError("");
        setCreateApiError({name: "", general: ""});
        setCreateForm({name: "", fullAddress: "", type: "", customer: ""});
    };

    const handleCloseEdit = () => {
        setEditOpen(false);
        setEditApiError({name: "", general: ""});
        setEditError("");
    };

    const handleCloseDelete = () => {
        setDeleteOpen(false);
        setDeleteError("");
    };

    const doCreate = async () => {
        setCreateApiError({name: "", general: ""});
        if (!createForm.name.trim() || !createForm.fullAddress.trim() || !createForm.type.trim() || !createForm.customer.trim()) {
            setCreateError(t("validation.requiredAll"));
            return;
        }
        const {lat, lng} = await fetchLatLng(createForm.fullAddress);
        const input = {
            name: createForm.name,
            address: {fullAddress: createForm.fullAddress, latitude: lat, longitude: lng},
            type: createForm.type,
            customer: createForm.customer,
        };
        try {
            setCreateError("");
            await createLocation({variables: {input}});
            handleCloseCreate();
            setSnackbar({open: true, message: t("locationPage.createSuccess"), severity: "success"});
            refetch();
        } catch (err) {
            handleGraphQLError(err, setCreateApiError, t);
        }
    };

    const doUpdate = async () => {
        setEditApiError({name: "", general: ""});
        const {id, ...rest} = editForm;
        if (!rest.name.trim() || !rest.fullAddress.trim() || !rest.type.trim() || !rest.customer.trim()) {
            setEditError(t("validation.requiredAll"));
            return;
        }
        let lat = rest.latitude;
        let lng = rest.longitude;


        if (rest.fullAddress?.trim()) {
            const coords = await fetchLatLng(rest.fullAddress);
            lat = coords.lat;
            lng = coords.lng;
        }

        const input = {
            name: rest.name,
            address: {fullAddress: rest.fullAddress, latitude: lat, longitude: lng},
            type: rest.type,
            customer: rest.customer,
        };
        try {
            setEditError("");
            await updateLocation({variables: {id, input}});
            handleCloseEdit();
            setSnackbar({open: true, message: t("locationPage.updateSuccess"), severity: "success"});
            refetch();
        } catch (err) {
            handleGraphQLError(err, setEditApiError, t);
        }
    };

    const doDelete = async () => {
        try {
            setDeleteError("");
            await deleteLocation({variables: {id: deleteId}});
            handleCloseDelete();
            setSnackbar({open: true, message: t("locationPage.deleteSuccess"), severity: "success"});
            refetch();
        } catch (err) {
            const msg = err?.graphQLErrors?.[0]?.message || t("error.deleteFailed");
            setDeleteError(msg);
        }
    };

    const onResetFilter = () => {
        setNameFilter("");
        setAddressFilter("");
        setTypeFilter("");
        setCustomerFilter("");
        setDebouncedFilters({name: "", address: "", type: "", customer: ""});
        setPage(0);
    };

    const onRefresh = () => refetch();

    const locationOptions = sortOptionsByLabel([
        {value: "", label: t?.("common.all") || "All"},
        {
            value: "HOME",
            label: t("locationPage.types.home"),
        },
        {
            value: "RESTAURANT",
            label: t("locationPage.types.restaurant"),
        },
        {
            value: "CAFE",
            label: t("locationPage.types.cafe"),
        },
        {
            value: "BAR",
            label: t("locationPage.types.bar"),
        },
        {
            value: "OFFICE",
            label: t("locationPage.types.office"),
        },
        {
            value: "SHOPPING_MALL",
            label: t("locationPage.types.shopping_mall"),
        },
        {
            value: "OTHER",
            label: t("locationPage.types.other"),
        }

    ], {preserveFirst: true});

    return (
        <Box>
            {!isLoaded ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "60vh",
                    }}
                >
                    <CircularProgress/>
                </Box>
            ) : (
                <Box>
                    {/* <Typography variant="h5" gutterBottom>{t("locationPage.title")}</Typography> */}
                    <PageHeader
                        title={t("locationPage.title")}
                        subtitle={t("locationPage.subtitle")}
                        icon={<LocationOnIcon/>}
                    />

                    <Paper elevation={3} sx={{borderRadius: 2, overflow: "hidden"}}>
                        <Toolbar
                            sx={{
                                px: 2,
                                py: 1,
                                gap: 1,
                                flexWrap: "wrap",
                                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                                bgcolor: "background.paper",
                            }}
                        >


                            <SearchField
                                label={t("locationPage.fields.name")}
                                value={nameFilter}
                                onChange={(e) => setNameFilter(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                placeholder={t("locationPage.search.namePlaceholder")}
                                isSmall={isSmall}
                            />

                            <SearchField
                                label={t("locationPage.fields.address")}
                                value={addressFilter}
                                onChange={(e) => setAddressFilter(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                placeholder={t("locationPage.search.addressPlaceholder")}
                                isSmall={isSmall}
                            />


                            <ResponsiveSelect
                                label={t("locationPage.fields.type")}
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                onClear={() => setTypeFilter("")}
                                options={locationOptions}
                                t={t}
                            />


                            {role !== "CUSTOMER" && (

                                <ResponsiveSelect
                                    label={t("locationPage.fields.customer")}
                                    value={customerFilter}
                                    onChange={(e) => setCustomerFilter(e.target.value)}
                                    onClear={() => setCustomerFilter("")}
                                    data={customerData}
                                    dataKey="filterCustomer"
                                    t={t}
                                    isSmall={isSmall}
                                />
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SearchIcon/>}
                                onClick={applyFilters}
                                sx={{
                                    height: 40,
                                    px: 2,
                                    textTransform: "none",
                                    fontWeight: 500,
                                    minWidth: isSmall ? "100%" : "auto",
                                }}
                            >
                                {t("common.search")}
                            </Button>

                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<ClearIcon/>}
                                onClick={() => {
                                    onResetFilter();
                                }}
                                sx={{
                                    height: 40,
                                    px: 2,
                                    textTransform: "none",
                                    fontWeight: 500,
                                    minWidth: isSmall ? "100%" : "auto",
                                }}
                            >
                                {t("common.clear")}
                            </Button>

                            <Box sx={{flex: 1}}/>


                            <Tooltip title={t("common.refresh")}>
                <span>
                  <IconButton onClick={onRefresh} aria-label="refresh">
                    <RefreshIcon/>
                  </IconButton>
                </span>
                            </Tooltip>

                            {role !== "CUSTOMER" && (
                                <Button variant="contained" startIcon={<AddIcon/>} onClick={() => setCreateOpen(true)}>
                                    {t("locationPage.createButton")}
                                </Button>
                            )}
                        </Toolbar>

                        {loading ? (
                            <Box sx={{p: 4, textAlign: "center"}}>
                                <CircularProgress/>
                            </Box>
                        ) : error ? (
                            <Box sx={{p: 2}}>
                                <Alert severity="error">{error.message}</Alert>
                            </Box>
                        ) : (
                            <>
                                <TableContainer sx={{maxHeight: 520}}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>

                                                <EllipsisCell sx={{bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary"}}>
                                                    <TableSortLabel
                                                        {...getSortProps("name")}
                                                        onClick={() => handleSort("name")}
                                                    >
                                                        {t("locationPage.fields.name")}
                                                    </TableSortLabel>
                                                </EllipsisCell>


                                                <EllipsisCell sx={{bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary"}}>
                                                    <TableSortLabel
                                                        {...getSortProps("addressFullAddress")}

                                                        onClick={() => handleSort("addressFullAddress")}
                                                    >
                                                        {t("locationPage.fields.address")}
                                                    </TableSortLabel>
                                                </EllipsisCell>


                                                <EllipsisCell sx={{bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary"}}>
                                                    <TableSortLabel
                                                        {...getSortProps("type")}

                                                        onClick={() => handleSort("type")}
                                                    >
                                                        {t("locationPage.fields.type")}
                                                    </TableSortLabel>
                                                </EllipsisCell>

                                                {role !== "CUSTOMER" && (
                                                    <EllipsisCell sx={{bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary"}}>
                                                        <TableSortLabel
                                                            {...getSortProps("customerName")}
                                                            onClick={() => handleSort("customerName")}
                                                        >
                                                            {t("locationPage.fields.customer")}
                                                        </TableSortLabel>
                                                    </EllipsisCell>
                                                )}

                                                <EllipsisCell sx={{bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary", width: "1%", whiteSpace: "nowrap"}}>
                                                    <TableSortLabel
                                                        {...getSortProps("updatedTimestamp")}
                                                        onClick={() => handleSort("updatedTimestamp")}
                                                    >
                                                        {t("userPage.table.updated")}
                                                    </TableSortLabel>
                                                </EllipsisCell>

                                                {canEdit && (
                                                    <EllipsisCell sx={{bgcolor: "grey.50", fontWeight: 600, fontSize: "0.875rem", borderBottom: "2px solid", borderBottomColor: "divider", color: "text.primary", width: "1%", whiteSpace: "nowrap"}}>
                                                        {t("userPage.table.actions")}
                                                    </EllipsisCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.length === 0 ? (
                                                <TableRow>
                                                    <EllipsisCell colSpan={emptyStateColumns} align="center">{t("userPage.noRecords")}</EllipsisCell>
                                                </TableRow>
                                            ) : (
                                                rows.map((r) => (
                                                    <TableRow
                                                        key={r.id}
                                                        hover
                                                        sx={{
                                                            "&:nth-of-type(even)": {
                                                                bgcolor: "grey.50"
                                                            },
                                                            "&:hover": {
                                                                bgcolor: "action.hover"
                                                            }
                                                        }}
                                                    >

                                                        <EllipsisCell>{displayValue(r.name)}</EllipsisCell>
                                                        <EllipsisCell>{displayValue(r.address?.fullAddress)}</EllipsisCell>

                                                        <EllipsisCell>{r.type
                                                            ? t(`locationPage.types.${r.type.trim().toLowerCase()}`)
                                                            : "-"}</EllipsisCell>


                                                        {role !== "CUSTOMER" && (
                                                            <EllipsisCell>{displayValue(r.customer?.name)}</EllipsisCell>

                                                        )}
                                                        <EllipsisCell sx={{whiteSpace: "nowrap"}}>
                                                            {r.updatedTimestamp ? formatToLocalTime(r.updatedTimestamp) : "-"}
                                                        </EllipsisCell>
                                                        {canEdit && (
                                                            <EllipsisCell sx={{whiteSpace: "nowrap"}}>
                                                                {r.address?.latitude && r.address?.longitude && (
                                                                    <Tooltip title={t("locationPage.map.showOnMap")}>
                                                                        <IconButton
                                                                            size="small"
                                                                            color="primary"
                                                                            onClick={() => openMapDialog(r)}
                                                                            sx={{p: 0.5}}
                                                                        >
                                                                            <PlaceIcon fontSize="small"/>
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                                <Tooltip title={t("common.update")}>
                                                                    <IconButton size="small" onClick={() => openEdit(r)}>
                                                                        <EditIcon/>
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title={t("common.delete")}>
                                                                    <IconButton size="small" color="error" onClick={() => openDelete(r)}>
                                                                        <DeleteIcon/>
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </EllipsisCell>
                                                        )}

                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    labelRowsPerPage={t("common.rowsPerPage")}
                                    component="div"
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    count={pageInfo?.totalElements ?? 0}
                                    rowsPerPage={pageInfo?.pageSize ?? rowsPerPage}
                                    page={pageInfo?.pageNumber ?? page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    ActionsComponent={TablePaginationActions}
                                />
                            </>
                        )}
                    </Paper>

                    <Dialog open={createOpen} onClose={handleCloseCreate} fullWidth maxWidth="sm">
                        <DialogTitle sx={{fontWeight: "bold"}}>{t("locationPage.createTitle")}</DialogTitle>
                        <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                        {createApiError.general && <Alert severity="error" sx={{mb: 2}}>{createApiError.general}</Alert>}
                        <DialogContent sx={{pt: 1}}>
                            <Stack spacing={2} sx={{mt: 1}}>
                                <TextField
                                    label={t("locationPage.fields.name")}
                                    value={createForm.name}
                                    onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                                    error={!!createApiError.name || (createError && !createForm.name.trim())}
                                    helperText={
                                        createApiError.name ||
                                        (createError && !createForm.name.trim() ? t("validation.requiredField") : "")
                                    }
                                    autoFocus
                                />

                                <AddressAutocomplete
                                    label={t("locationPage.fields.fullAddress")}
                                    isLoaded={isLoaded}
                                    value={createForm.fullAddress}
                                    error={createError && !createForm.fullAddress.trim()}
                                    helperText={createError && !createForm.fullAddress.trim() ? t("validation.requiredField") : ""}
                                    onSelect={(address, lat, lng) =>
                                        setCreateForm({
                                            ...createForm,
                                            fullAddress: address,
                                            latitude: lat,
                                            longitude: lng,
                                        })
                                    }
                                />

                                <ResponsiveSelect
                                    label={t("locationPage.fields.type")}
                                    value={createForm.type}
                                    onChange={(e) => setCreateForm({...createForm, type: e.target.value})}
                                    fullWidth={true}
                                    options={locationOptions}
                                    error={(createError && !createForm.type.trim())}
                                    helperText={
                                        createError && !createForm.type.trim() ? t("validation.requiredField") : ""
                                    }
                                    t={t}
                                />


                                <ResponsiveSelect
                                    label={t("locationPage.fields.customer")}
                                    value={createForm.customer}
                                    onChange={(e) => setCreateForm({...createForm, customer: e.target.value})}
                                    data={customerData}
                                    dataKey="filterCustomer"
                                    error={!!createError && !createForm.customer.trim()}
                                    helperText={
                                        createError && !createForm.customer.trim() ? t("validation.requiredField") : ""
                                    }
                                    fullWidth={true}
                                />

                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseCreate}>{t("common.cancel")}</Button>
                            <Button onClick={doCreate} disabled={creating} aria-busy={creating} variant="contained">
                                {creating ? <CircularProgress size={20}/> : t("common.create")}
                            </Button>
                        </DialogActions>
                    </Dialog>


                    <Dialog open={editOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
                        <DialogTitle sx={{fontWeight: "bold"}}>{t("locationPage.updateTitle")}</DialogTitle>
                        <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                        {editApiError.general && <Alert severity="error" sx={{mb: 2}}>{editApiError.general}</Alert>}
                        <DialogContent sx={{pt: 1}}>
                            <Stack spacing={2} sx={{mt: 1}}>
                                <TextField
                                    label={t("locationPage.fields.name")}
                                    value={editForm.name}
                                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                                    error={!!editApiError.name || (editError && !editForm.name.trim())}
                                    helperText={
                                        editApiError.name ||
                                        (editError && !editForm.name.trim() ? t("validation.requiredField") : "")
                                    }
                                    autoFocus
                                />
                                <AddressAutocomplete
                                    label={t("locationPage.fields.fullAddress")}
                                    value={editForm.fullAddress}
                                    isLoaded={isLoaded}
                                    error={!!editApiError.fullAddress || (editError && !editForm.fullAddress.trim())}
                                    latitude={editForm.latitude}
                                    longitude={editForm.longitude}
                                    helperText={
                                        editApiError.fullAddress ||
                                        (editError && !editForm.fullAddress.trim() ? t("validation.requiredField") : "")
                                    }
                                    onSelect={(address, lat, lng) =>
                                        setEditForm({
                                            ...editForm,
                                            fullAddress: address,
                                            latitude: lat,
                                            longitude: lng,
                                        })
                                    }
                                />

                                <ResponsiveSelect
                                    label={t("locationPage.fields.type")}
                                    value={editForm.type}
                                    onChange={(e) => setEditForm({...editForm, type: e.target.value})}
                                    fullWidth={true}
                                    options={locationOptions}
                                    error={(editError && !editForm.type.trim())}
                                    helperText={
                                        editError && !editForm.type.trim() ? t("validation.requiredField") : ""
                                    }
                                    t={t}
                                />


                                <ResponsiveSelect
                                    label={t("locationPage.fields.customer")}
                                    value={editForm.customer}
                                    onChange={(e) => setEditForm({...editForm, customer: e.target.value})}
                                    data={customerData}
                                    dataKey="filterCustomer"
                                    error={(editError && !editForm.customer.trim())}
                                    helperText={
                                        editError && !editForm.customer.trim() ? t("validation.requiredField") : ""
                                    }
                                    fullWidth={true}
                                />
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseEdit}>{t("common.cancel")}</Button>
                            <Button onClick={doUpdate} disabled={updating} aria-busy={updating} variant="contained">
                                {updating ? <CircularProgress size={20}/> : t("common.update")}
                            </Button>
                        </DialogActions>
                    </Dialog>


                    <Dialog open={deleteOpen} onClose={handleCloseDelete}>
                        <DialogTitle sx={{fontWeight: "bold"}}>{t("locationPage.deleteConfirmTitle")}</DialogTitle>
                        <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                        {deleteError && <Alert severity="error" sx={{mb: 2}}>{deleteError}</Alert>}
                        <DialogContent>
                            <Typography variant="h6" sx={{color: "#d32f2f", fontWeight: "bold", mt: 1}}>
                                {editForm.name}
                            </Typography>
                            <Typography>{t("locationPage.deleteConfirmMessage")}</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDelete}>{t("common.cancel")}</Button>
                            <Button onClick={doDelete} color="error" disabled={deleting} aria-busy={deleting} variant="contained">
                                {deleting ? <CircularProgress size={20}/> : t("common.delete")}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={mapDialogOpen}
                        onClose={closeMapDialog}
                        fullWidth
                        maxWidth="sm"
                        PaperProps={{sx: {borderRadius: 2}}}
                    >
                        <DialogTitle sx={{fontWeight: "bold"}}>

                            {selectedLocation?.name || t("locationPage.map.location")} — {t("locationPage.map.map")}
                        </DialogTitle>
                        <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                        <DialogContent sx={{height: 400, p: 0}}>
                            {selectedLocation?.address?.latitude && selectedLocation?.address?.longitude ? (
                                <GoogleMap
                                    mapContainerStyle={{width: "100%", height: "100%"}}
                                    center={{
                                        lat: selectedLocation.address.latitude,
                                        lng: selectedLocation.address.longitude,
                                    }}
                                    zoom={16}
                                    options={{
                                        disableDefaultUI: true,
                                        gestureHandling: "cooperative",
                                    }}
                                >
                                    <Marker
                                        position={{
                                            lat: selectedLocation.address.latitude,
                                            lng: selectedLocation.address.longitude,
                                        }}
                                    />
                                </GoogleMap>
                            ) : (
                                <Box
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography>{t("locationPage.map.noCoordinates")}</Typography>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeMapDialog}>{t("common.close")}</Button>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        TransitionComponent={SlideUp}
                        open={snackbar.open}
                        autoHideDuration={2500}
                        onClose={() => setSnackbar({...snackbar, open: false})}
                        anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                        sx={{
                            "& .MuiAlert-root": {
                                borderRadius: 2,
                                boxShadow: "0px 4px 12px rgba(0,0,0,0.25)",
                                fontWeight: 500,
                                minWidth: 260,
                                justifyContent: "center",
                            },
                        }}
                    >
                        <Alert
                            severity={snackbar.severity}
                            onClose={() => setSnackbar({...snackbar, open: false})}
                            variant="filled"
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Box>
            )}
        </Box>
    );


};

export default Location;
